import { useState } from "react";
import ChartCard from "../../components/ui/ChartCard";
import { CeoTextPanel } from "../../components/ui/CeoTextAnswers";
import { buildRadarOptions } from "./chartOptions";

const buildDataSource = (data = {}) => {
  const metrics = data?.ervaringMetrics || {};

  const hulpProces = Array.isArray(metrics.hulp_proces) ? metrics.hulp_proces : [];

  const hulpVooralOm = Array.isArray(metrics.hulp_vooral_om)
    ? metrics.hulp_vooral_om
    : [];

  const effectHulp = Array.isArray(metrics.effect_hulp) ? metrics.effect_hulp : [];

  const mijBegrijpen = Array.isArray(metrics.mij_begrijpen)
    ? metrics.mij_begrijpen
    : [];

  const timeline = Array.isArray(metrics.ervaring_timeline)
    ? metrics.ervaring_timeline
    : [];

  return {
    hulpProces,
    hulpVooralOm,
    effectHulp,
    mijBegrijpen,
    timeline,
  };
};

const normalizeRadialData = (items = []) => {
  const labels = items.map((item) => item.label);
  const absoluteValues = items.map((item) => Number(item?.count) || 0);
  const total = absoluteValues.reduce((acc, value) => acc + Math.max(value, 0), 0);

  const percentageSeries =
    total > 0
      ? absoluteValues.map((value) => Number(((value / total) * 100).toFixed(2)))
      : absoluteValues.map(() => 0);

  return {
    labels,
    absoluteValues,
    percentageSeries,
    total,
  };
};

const buildRadialOptions = (
  labels,
  displayValues = [],
  percentageValues = [],
  activeIndex = null,
  setActiveIndex
) => {
  const absolute = displayValues.map((value) => Number(value) || 0);
  const percentages = percentageValues.map((value) => Number(value) || 0);
  const totalAbsolute = absolute.reduce((acc, value) => acc + value, 0);

  const resolvedIndex =
    Number.isInteger(activeIndex) && activeIndex >= 0 && activeIndex < absolute.length
      ? activeIndex
      : null;

  const currentLabel = resolvedIndex !== null ? labels[resolvedIndex] : "Totaal";
  const activeAbsolute =
    resolvedIndex !== null && Number.isFinite(absolute[resolvedIndex])
      ? absolute[resolvedIndex]
      : null;
  const activePercentage =
    resolvedIndex !== null && Number.isFinite(percentages[resolvedIndex])
      ? percentages[resolvedIndex]
      : null;
  const currentValue =
    activeAbsolute !== null
      ? `${activeAbsolute}${Number.isFinite(activePercentage) ? ` (${activePercentage.toFixed(1)}%)` : ""
      }`
      : totalAbsolute;

  return {
    chart: {
      type: "radialBar",
      events: {
        dataPointMouseEnter: (_event, _chartContext, config) => {
          if (typeof setActiveIndex === "function") {
            setActiveIndex(
              Number.isInteger(config?.dataPointIndex)
                ? config.dataPointIndex
                : null
            );
          }
        },
        dataPointMouseLeave: () => {
          if (typeof setActiveIndex === "function") {
            setActiveIndex(null);
          }
        },
        mouseLeave: () => {
          if (typeof setActiveIndex === "function") {
            setActiveIndex(null);
          }
        },
      },
    },
    plotOptions: {
      radialBar: {
        dataLabels: {
          name: {
            fontSize: "16px",
            formatter: () => currentLabel || "",
          },
          value: {
            fontSize: "14px",
            formatter: () => currentValue,
          },
          total: {
            show: resolvedIndex === null,
            label: "Totaal",
            formatter: () => totalAbsolute,
          },
        },
      },
    },
    labels: labels || [],
    tooltip: {
      y: {
        formatter: (_value, opts) => {
          const dataIndex =
            Number.isInteger(opts?.dataPointIndex) && opts.dataPointIndex >= 0
              ? opts.dataPointIndex
              : 0;
          const absoluteValue =
            Number.isFinite(absolute[dataIndex]) ? absolute[dataIndex] : 0;
          const percentage =
            Number.isFinite(percentages[dataIndex]) ? percentages[dataIndex] : null;
          if (Number.isFinite(percentage)) {
            return `${absoluteValue} (${percentage.toFixed(1)}%)`;
          }
          return `${absoluteValue}`;
        },
      },
    },
  };
};

export default function ErvaringTab({ data, showTextAnswers = false }) {
  const textAnswers = data?.ceoTextAnswers || {};
  const { hulpProces, hulpVooralOm, effectHulp, mijBegrijpen, timeline } =
    buildDataSource(data);
  const [activeHulpProcesIndex, setActiveHulpProcesIndex] = useState(null);
  const [activeHulpVooralIndex, setActiveHulpVooralIndex] = useState(null);
  const [activeEffectIndex, setActiveEffectIndex] = useState(null);

  const {
    labels: hulpProcesLabels,
    absoluteValues: hulpProcesAbsolute,
    percentageSeries: hulpProcesSeries,
  } = normalizeRadialData(hulpProces);

  const {
    labels: hulpVooralLabels,
    absoluteValues: hulpVooralAbsolute,
    percentageSeries: hulpVooralSeries,
  } = normalizeRadialData(hulpVooralOm);

  const {
    labels: effectLabels,
    absoluteValues: effectAbsolute,
    percentageSeries: effectSeries,
  } = normalizeRadialData(effectHulp);

  const mijBegrijpenLabels = mijBegrijpen.map((item) => item.label);
  const mijBegrijpenValues = mijBegrijpen.map((item) =>
    Number.isFinite(item?.average_score) ? Number(item.average_score.toFixed(2)) : 0
  );
  const mijBegrijpenCounts = mijBegrijpen.map((item) => Number(item?.count) || 0);

  const mijBegrijpenOptions = buildRadarOptions({
    categories: mijBegrijpenLabels,
    yaxis: {
      show: true,
      min: 0,
      max: 10,
      tickAmount: 5,
      labels: {
        formatter: (val) => Math.round(val),
      },
    },
    tooltipFormatter: (val, opts) => {
      const idx =
        Number.isInteger(opts?.dataPointIndex) && opts.dataPointIndex >= 0
          ? opts.dataPointIndex
          : 0;
      const count = mijBegrijpenCounts[idx] ?? 0;
      const value = typeof val === "number" ? val.toFixed(2) : val;
      return `Gemiddelde: ${value} (${count} antwoorden)`;
    },
  });
  const mijBegrijpenSeries = [{ name: "Gemiddelde score", data: mijBegrijpenValues }];

  const scatterPoints = timeline
    .filter(
      (point) =>
        Number(point?.count) > 0 && Number.isFinite(point?.average_score)
    )
    .map((point) => ({
      x: point.label,
      y: Number(point.average_score.toFixed(2)),
      count: point.count,
    }));

  const scatterSeries = [
    {
      name: "Gem. ervaring",
      data: scatterPoints,
    },
  ];

  const scatterOptions = {
    chart: {
      type: "scatter",
      zoom: { enabled: false },
      toolbar: { show: false },
    },
    xaxis: {
      title: { text: "Wanneer ervaring" },
      labels: { rotate: -20 },
    },
    yaxis: {
      title: { text: "Gemiddelde ervaring (1-5)" },
      min: 1,
      max: 5,
      tickAmount: 4,
    },
    tooltip: {
      custom: ({ seriesIndex, dataPointIndex, w }) => {
        const point =
          w?.config?.series?.[seriesIndex]?.data?.[dataPointIndex] || {};
        const score =
          typeof point.y === "number" ? point.y.toFixed(2) : "n.v.t.";
        return `
          <div class="apexcharts-tooltip-title">${point.x ?? ""}</div>
          <div class="apexcharts-tooltip-text">
            Gem. score: ${score}<br/>
            Antwoorden: ${point.count ?? 0}
          </div>`;
      },
    },
  };

  return (
    <div>
      <div className="row row-3">
        <ChartCard
          title="Hulp proces"
          options={buildRadialOptions(
            hulpProcesLabels,
            hulpProcesAbsolute,
            hulpProcesSeries,
            activeHulpProcesIndex,
            setActiveHulpProcesIndex
          )}
          series={hulpProcesSeries}
          type="radialBar"
          info="Het zoeken en krijgen van de juiste hulp ging?"
        />
        <ChartCard
          title="Gekregen hulp ging vooral om"
          options={buildRadialOptions(
            hulpVooralLabels,
            hulpVooralAbsolute,
            hulpVooralSeries,
            activeHulpVooralIndex,
            setActiveHulpVooralIndex
          )}
          series={hulpVooralSeries}
          type="radialBar"
          info="Bij de hulp die ik kreeg ging het vooral om?"
        />
        <ChartCard
          title="Effect gekregen hulp"
          options={buildRadialOptions(
            effectLabels,
            effectAbsolute,
            effectSeries,
            activeEffectIndex,
            setActiveEffectIndex
          )}
          series={effectSeries}
          type="radialBar"
          info="Het effect van de hulp die ik kreeg is uiteindelijk"
        />
      </div>

      <div className="row row-split">
        <ChartCard
          title="Ervaringsvragen (tijdlijn)"
          options={scatterOptions}
          series={scatterSeries}
          type="scatter"
          info="Combinatie: Wanneer was deze ervaring & Hoe was je ervaring?"
        />
        <ChartCard
          title="Bejegening"
          options={mijBegrijpenOptions}
          series={mijBegrijpenSeries}
          type="radar"
          info="Gemiddelde score (0-10) per vraag."
        />
      </div>
      {showTextAnswers && <CeoTextPanel title="Iets kwijt over je ervaring" items={textAnswers.iets_kwijt_ervaring || []} onPage />}
    </div>
  );
}
